import { useMemo } from 'react';

const addDays = (iso, n) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export function useProactiveInsight({ workouts = [], events = [], goals = [], today }) {
  return useMemo(() => {
    // ── 1. Race within 7 days ──────────────────────────────────────────────────
    const raceGoal = goals.find(g =>
      g.targetDate && (g.type === 'race' || /race|marathon|half|5k|10k|triathlon/i.test(g.title || ''))
    );
    if (raceGoal?.targetDate) {
      const days = Math.round(
        (new Date(raceGoal.targetDate + 'T12:00:00') - new Date(today + 'T12:00:00')) / 86400000
      );
      if (days >= 0 && days <= 7) {
        const name = raceGoal.title?.toLowerCase() || 'your race';
        return {
          type: 'race',
          headline: `${days} days to ${name}`,
          body: 'taper week — keep today easy. no new stress on the legs.',
          cta: 'see taper plan →',
          coachMsg: `I have ${days} days until ${name}. What should my taper look like this week?`,
        };
      }
    }

    // ── 2. 4+ consecutive training days ───────────────────────────────────────
    let streak = 0;
    for (let i = 0; i < 8; i++) {
      const d = addDays(today, -i);
      if (workouts.some(w => w.date === d && w.type !== 'Rest')) streak++;
      else break;
    }
    if (streak >= 4) {
      return {
        type: 'recovery',
        headline: `${streak} days straight`,
        body: 'your body adapts during rest. consider an easy day or full rest tomorrow.',
        cta: 'log rest day →',
        coachMsg: `I've trained ${streak} days in a row. Should I take a rest day tomorrow?`,
      };
    }

    // ── 3. Missed planned workout yesterday ────────────────────────────────────
    const yesterday = addDays(today, -1);
    const plannedYesterday = events.find(e => e.date === yesterday && e.type === 'health');
    const loggedYesterday  = workouts.some(w => w.date === yesterday);
    if (plannedYesterday && !loggedYesterday) {
      const name = plannedYesterday.title?.toLowerCase() || 'workout';
      return {
        type: 'missed',
        headline: `missed yesterday's ${name}`,
        body: 'no stress — want to move it to today or drop it?',
        cta: 'ask coach →',
        coachMsg: `I missed my ${name} yesterday. Should I do it today or skip it?`,
      };
    }

    // ── 4. Post-night-shift + training today ──────────────────────────────────
    const isNightShift = (e) => {
      if (!e) return false;
      if (e.type === 'work' || /shift/i.test(e.title || '')) {
        const t = e.time || '';
        const hour = parseInt(t.split(':')[0], 10);
        return !isNaN(hour) && (hour >= 18 || hour < 8);
      }
      return false;
    };
    const nightShift = events.find(e =>
      (e.date === yesterday || e.date === today) && isNightShift(e)
    );
    const trainingToday = events.find(e => e.date === today && e.type === 'health');
    if (nightShift && trainingToday) {
      return {
        type: 'shift',
        headline: 'post-night-shift today',
        body: 'strength session scheduled — consider moving to tomorrow.',
        cta: 'adjust plan →',
        coachMsg: 'I worked a night shift and have a workout scheduled today. Should I move it?',
      };
    }

    // ── 5. New weekly mileage PR ───────────────────────────────────────────────
    const getWeekMiles = (weekStart) => {
      const end = addDays(weekStart, 6);
      return workouts
        .filter(w => w.date >= weekStart && w.date <= end)
        .reduce((s, w) => s + parseFloat(w.dist || 0), 0);
    };
    // Current week start (Monday)
    const nowD   = new Date(today + 'T12:00:00');
    const dow    = (nowD.getDay() + 6) % 7; // 0=Mon
    const monIso = addDays(today, -dow);
    const thisMi = getWeekMiles(monIso);
    // Best of last 12 weeks (excluding current)
    let maxPrev = 0;
    for (let w = 1; w <= 12; w++) {
      const ws = addDays(monIso, -7 * w);
      maxPrev = Math.max(maxPrev, getWeekMiles(ws));
    }
    if (thisMi > 0 && thisMi > maxPrev && maxPrev > 0) {
      return {
        type: 'pr',
        headline: 'new weekly mileage record',
        body: `${thisMi.toFixed(1)} miles this week — your highest ever. recovery matters now.`,
        cta: null,
        coachMsg: null,
      };
    }

    return null;
  }, [workouts, events, goals, today]);
}
